<div class="container my-4">
  <div class="d-flex justify-content-between align-items-center mb-4">
    <div>
      <h2 class="fw-bold text-dark mb-1">Service Billing Details</h2>
      <p class="text-muted small mb-0">
        View comprehensive service billing information
      </p>
    </div>
    <button class="btn btn-outline-secondary btn-sm" (click)="goBack()">
      ← Back
    </button>
  </div>

  @if (billing) {
  <div>
    <div class="card shadow-sm mb-4 border-0">
      <div class="card-header bg-white border-bottom pb-3">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h4 class="fw-bold mb-1" style="color: #37bc6f">
              Service No. {{ billing.serviceNumber || 'N/A' }}
            </h4>
            <small class="text-muted">{{
              billing.createdAt | date : "fullDate"
              }}</small>
          </div>
          <span class="badge fs-6 px-3 py-2 rounded-pill bg-info">
            Service Bill
          </span>
        </div>
      </div>
      <div class="card-body">
        <div class="row g-4">
          <div class="col-md-3">
            <div class="border-start border-primary border-4 ps-3">
              <small class="text-muted text-uppercase fw-semibold">Grand Total</small>
              <p class="mb-0 fw-semibold h5 text-primary">
                {{ formatCurrency(billing.total) }}
              </p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border-start border-info border-4 ps-3">
              <small class="text-muted text-uppercase fw-semibold">Discount</small>
              <p class="mb-0 fw-semibold">
                {{ formatCurrency(billing.discount || 0) }}
              </p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border-start border-warning border-4 ps-3">
              <small class="text-muted text-uppercase fw-semibold">Total GST</small>
              <p class="mb-0 fw-semibold">
                {{ formatCurrency(billing.gstAmount || 0) }}
              </p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="border-start border-secondary border-4 ps-3">
              <small class="text-muted text-uppercase fw-semibold">Recipient</small>
              <p class="mb-0 fw-semibold">
                {{ billing.recipient?.name || 'N/A' }}
              </p>
            </div>
          </div>
        </div>

        @if (billing.notes) {
        <div class="row mt-4">
          <div class="col-12">
            <div class="alert alert-light border-start border-success border-4 shadow-sm mb-0">
              <h6 class="fw-bold text-success mb-2"><i class="bi bi-sticky"></i> Billing Notes</h6>
              <p class="mb-0 text-dark">{{ billing.notes }}</p>
            </div>
          </div>
        </div>
        }
      </div>
    </div>

    <div class="card shadow-sm border-0">
      <div class="card-header bg-light d-flex justify-content-between align-items-center">
        <h6 class="fw-bold mb-0 text-dark">📋 Service Items List</h6>
        <span class="badge bg-secondary rounded-pill">
          {{ billing?.items?.length || 0 }} Items
        </span>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th class="fw-semibold text-muted border-0 py-3">#</th>
                <th class="fw-semibold text-muted border-0 py-3">Service Name</th>
                <th class="fw-semibold text-muted border-0 py-3">Provider</th>
                <th class="fw-semibold text-muted border-0 py-3 text-end">Line Total</th>
              </tr>
            </thead>
            <tbody>
              @for (item of billing?.items; track item; let i = $index) {
              <tr class="border-bottom">
                <td class="py-3 text-muted fw-semibold">{{ i + 1 }}</td>
                <td class="py-3 fw-semibold">
                  {{ item?.service?.name }}
                </td>
                <td class="py-3">
                  @if (item.provider) {
                  <span class="badge bg-light text-dark border">{{ item.provider.name }}</span>
                  } @else {
                  <span class="text-muted">N/A</span>
                  }
                </td>
                <td class="py-3 text-end">
                  {{ formatCurrency(item.total) }}
                </td>
              </tr>
              }
              @if (!billing?.items?.length) {
              <tr>
                <td colspan="4" class="text-center py-5">
                  <div class="text-muted">
                    <div class="display-4 mb-3">⚙️</div>
                    <p class="fw-semibold mb-1">No services available</p>
                    <small>This bill doesn't contain any services yet.</small>
                  </div>
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0 mt-4">
      <div class="card-header bg-light">
        <h6 class="fw-bold mb-0 text-dark">💳 Payment Details</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table mb-0">
            <thead class="table-light">
              <tr>
                <th class="fw-semibold text-muted border-0 py-3">Mode</th>
                <th class="fw-semibold text-muted border-0 py-3 text-end">Amount</th>
                <th class="fw-semibold text-muted border-0 py-3 text-end">Cash Tendered</th>
              </tr>
            </thead>
            <tbody>
              @for (payment of billing?.serviceInvoicePayments; track payment) {
              <tr>
                <td class="py-3 fw-semibold">{{ payment.mode }}</td>
                <td class="py-3 text-end">{{ formatCurrency(payment.amount) }}</td>
                <td class="py-3 text-end">{{ formatCurrency(payment.cashTendered || 0) }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  }

  @if (!billing && isLoading) {
  <div class="text-center py-5">
    <div class="spinner-border text-primary mb-3" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="text-muted fw-semibold">Loading service billing details...</p>
  </div>
  }

  @if (!billing && !isLoading) {
  <div class="text-center py-5">
    <div class="display-4 text-danger mb-3">⚠️</div>
    <p class="text-muted fw-semibold">Service Billing record not found.</p>
    <p class="small text-muted">
      Please check the service bill number and try again.
    </p>
  </div>
  }
</div>
